import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { useAvailabilities } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { ExpensesUpsell } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/Table/components/upsells/Expenses'
import {
  FinancialWorkspaceResource,
  HookData,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

import { useFinanceResource } from './useFinanceResource'
import { FinanceFactSummary, formatData, useFinanceSummaries } from './useFinanceSummaries'
import { useSourcesVisibility } from './useSourcesVisibility'

interface Props {
  skip?: boolean
  resource?: FinancialWorkspaceResource
}

export const useExpensesData: (props?: Props) => HookData = (props) => {
  const { hasComparisonDate } = useFinancialWorkspace()
  const { canSeeEmployeeExpenses } = useSourcesVisibility()
  const { availabilities } = useAvailabilities()

  const { resource } = props ?? {}

  const loadingAvailabilities = availabilities.loading

  const seeUpsell = !availabilities.modules.expenses && !loadingAvailabilities

  const skip =
    !canSeeEmployeeExpenses ||
    !availabilities.modules.expenses ||
    loadingAvailabilities ||
    !!props?.skip

  const { data, loading, fetchFinanceSummaries, refetch } = useFinanceSummaries({
    skip: skip,
    resource: resource,
    financeTypes: ['expense'],
  })

  const {
    data: comparisonData,
    loading: comparisonLoading,
    refetch: comparisonRefetch,
    fetchFinanceSummaries: fetchComparisonFinanceSummaries,
  } = useFinanceSummaries({
    skip: skip,
    compareTo: true,
    resource: props?.resource,
    financeTypes: ['expense'],
  })

  const { onLazyChildren } = useFinanceResource({
    resource: resource,
    financeType: 'expense',
    childrenType: InsightTableGroupBy.CustomCategory,
  })

  useUnsafeEffect(() => {
    if (
      canSeeEmployeeExpenses &&
      availabilities.modules.expenses &&
      !loadingAvailabilities &&
      !props?.skip
    ) {
      fetchFinanceSummaries()
      if (hasComparisonDate) fetchComparisonFinanceSummaries()
    }
  }, [
    props?.skip,
    hasComparisonDate,
    loadingAvailabilities,
    fetchFinanceSummaries,
    canSeeEmployeeExpenses,
    fetchComparisonFinanceSummaries,
    availabilities.modules.expenses,
  ])

  const onRefetch = useUnsafeCallback(() => {
    refetch()
    if (hasComparisonDate) comparisonRefetch()
  }, [refetch, comparisonRefetch, hasComparisonDate])

  if (!canSeeEmployeeExpenses) {
    return
  }

  const response = () => {
    if (seeUpsell) {
      return [
        {
          id: `expenses_data`,
          customRow: {
            component: ExpensesUpsell,
            visible: true,
          },
          loading: false,
          data: [],
        },
      ]
    }

    const expensesData: FinanceFactSummary = data?.[0]
    const expensesComparisonData: FinanceFactSummary = comparisonData?.[0]

    return formatData({
      onLazyChildren,
      data: expensesData,
      label: expensesData?.label ?? '',
      comparisonData: expensesComparisonData,
      id: resource ? String(resource.id) : 'expenses_data',
      loading: loading || loadingAvailabilities || (hasComparisonDate && comparisonLoading),
    })
  }

  return {
    data: response(),
    refetch: onRefetch,
    visible: canSeeEmployeeExpenses,
    loading: loading || loadingAvailabilities || (hasComparisonDate && comparisonLoading),
  }
}
